import { useEffect } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { MainContents } from '../../../../components/MainContents';
import GeneralSettings from './GeneralSettings';
import AccountSettings from './AccountSettings';
import MemberSettings from './MemberSettings';
import ApiSettings from './ApiSettings';
import PortalSettings from './PortalSettings';
import useFetchIam from '../../api/useFetchIam';
import type { Configuration } from '../types/Organization';

export type OrganizationTabType = 'general' | 'portal' | 'account' | 'members' | 'api';
export type ApiIntegrationTabType = 'api' | 'ai';

/** トライアルプランで非表示にするタブ */
const TRIAL_HIDDEN_TABS: OrganizationTabType[] = ['general', 'api'];

interface Props {
  initialSettings?: Configuration;
}

export default function OrganizationEditPage({ initialSettings }: Props) {
  const navigate = useNavigate({ from: '/agent/settings' });
  const search = useSearch({ from: '/agent/settings/' });
  const currentTab: OrganizationTabType = search.t ?? 'general';
  const { data: iam } = useFetchIam();
  const isTrial = iam?.plan_type === 'trial';

  // トライアルプランで非表示タブが選択されていた場合、アカウント設定にリダイレクト
  useEffect(() => {
    if (isTrial && TRIAL_HIDDEN_TABS.includes(currentTab)) {
      navigate({
        to: '/agent/settings',
        search: (prev) => ({ ...prev, t: 'account' }),
        replace: true,
      });
    }
  }, [isTrial, currentTab, navigate]);

  const renderContent = () => {
    // トライアルプランでは非表示タブのコンテンツを描画しない
    if (isTrial && TRIAL_HIDDEN_TABS.includes(currentTab)) {
      return null;
    }

    switch (currentTab) {
      case 'general':
        return <GeneralSettings initialSettings={initialSettings} />;
      case 'account':
        return <AccountSettings />;
      case 'members':
        return <MemberSettings />;
      case 'portal':
        return <PortalSettings />;
      case 'api':
        return <ApiSettings />;
      default:
        return null;
    }
  };

  return <MainContents>{renderContent()}</MainContents>;
}
